import { NormalizedAttributeName, NormalizedTagName } from './normalizedstring';
import { RestrictedAttr, RestrictedAttrValues } from './restricted_attrs';

/**
 * List of keys not on the handler types but detected in Chrome v101 via:
 * JSON.stringify([...Object.getOwnPropertyNames(window).filter(e=>e.match(/^HTML.*Element$/)).reduce((e,t)=>(e.push(...Object.keys(window[t].prototype).filter(e=>e.startsWith("on"))),e),[]).reduce((e,t)=>e.add(t),new Set)]);
 */
type extraDetectedKeys = 'onenterpictureinpicture' | 'onleavepictureinpicture' | 'onencrypted' | 'onwaitingforkey' | 'onbeforexrselect' | 'oncancel' | 'oncontextlost' | 'oncontextrestored' | 'onmousewheel' | 'onpointerrawupdate' | 'onformdata' | 'onslotchange' | 'onwebkitanimationend' | 'onwebkitanimationiteration' | 'onwebkitanimationstart' | 'onwebkittransitionend' | 'ongamepadconnected' | 'ongamepaddisconnected';

type AllEventTypes = keyof (GlobalEventHandlers & WindowEventHandlers & DocumentEventMap & ElementEventMap) | extraDetectedKeys;

/**
 * This maps any bad attribute to a boolean (boolean value doesn't matter);
 * if new attributes are added on the *Handlers above, we should get a compile error here
 */
const badAttributeMap: { [K in AllEventTypes]: boolean } = {
  DOMContentLoaded: false,
  abort: false,
  addEventListener: false,
  animationcancel: false,
  animationend: false,
  animationiteration: false,
  animationstart: false,
  auxclick: false,
  beforeinput: false,
  beforetoggle: false,
  blur: false,
  cancel: false,
  canplay: false,
  canplaythrough: false,
  change: false,
  click: false,
  close: false,
  compositionend: false,
  compositionstart: false,
  compositionupdate: false,
  contextlost: false,
  contextmenu: false,
  contextrestored: false,
  copy: false,
  cuechange: false,
  cut: false,
  dblclick: false,
  drag: false,
  dragend: false,
  dragenter: false,
  dragleave: false,
  dragover: false,
  dragstart: false,
  drop: false,
  durationchange: false,
  emptied: false,
  ended: false,
  error: false,
  focus: false,
  focusin: false,
  focusout: false,
  formdata: false,
  fullscreenchange: false,
  fullscreenerror: false,
  gotpointercapture: false,
  input: false,
  invalid: false,
  keydown: false,
  keypress: false,
  keyup: false,
  load: false,
  loadeddata: false,
  loadedmetadata: false,
  loadstart: false,
  lostpointercapture: false,
  mousedown: false,
  mouseenter: false,
  mouseleave: false,
  mousemove: false,
  mouseout: false,
  mouseover: false,
  mouseup: false,
  onabort: false,
  onafterprint: false,
  onanimationcancel: false,
  onanimationend: false,
  onanimationiteration: false,
  onanimationstart: false,
  onauxclick: false,
  onbeforeinput: false,
  onbeforeprint: false,
  onbeforetoggle: false,
  onbeforeunload: false,
  onbeforexrselect: false,
  onblur: false,
  oncancel: false,
  oncanplay: false,
  oncanplaythrough: false,
  onchange: false,
  onclick: false,
  onclose: false,
  oncontextlost: false,
  oncontextmenu: false,
  oncontextrestored: false,
  oncopy: false,
  oncuechange: false,
  oncut: false,
  ondblclick: false,
  ondrag: false,
  ondragend: false,
  ondragenter: false,
  ondragleave: false,
  ondragover: false,
  ondragstart: false,
  ondrop: false,
  ondurationchange: false,
  onemptied: false,
  onencrypted: false,
  onended: false,
  onenterpictureinpicture: false,
  onerror: false,
  onfocus: false,
  onformdata: false,
  ongamepadconnected: false,
  ongamepaddisconnected: false,
  ongotpointercapture: false,
  onhashchange: false,
  oninput: false,
  oninvalid: false,
  onkeydown: false,
  onkeypress: false,
  onkeyup: false,
  onlanguagechange: false,
  onleavepictureinpicture: false,
  onload: false,
  onloadeddata: false,
  onloadedmetadata: false,
  onloadstart: false,
  onlostpointercapture: false,
  onmessage: false,
  onmessageerror: false,
  onmousedown: false,
  onmouseenter: false,
  onmouseleave: false,
  onmousemove: false,
  onmouseout: false,
  onmouseover: false,
  onmouseup: false,
  onmousewheel: false,
  onoffline: false,
  ononline: false,
  onpagehide: false,
  onpagereveal: false,
  onpageshow: false,
  onpageswap: false,
  onpaste: false,
  onpause: false,
  onplay: false,
  onplaying: false,
  onpointercancel: false,
  onpointerdown: false,
  onpointerenter: false,
  onpointerleave: false,
  onpointermove: false,
  onpointerout: false,
  onpointerover: false,
  onpointerrawupdate: false,
  onpointerup: false,
  onpopstate: false,
  onprogress: false,
  onratechange: false,
  onrejectionhandled: false,
  onreset: false,
  onresize: false,
  onscroll: false,
  onscrollend: false,
  onsecuritypolicyviolation: false,
  onseeked: false,
  onseeking: false,
  onselect: false,
  onselectionchange: false,
  onselectstart: false,
  onslotchange: false,
  onstalled: false,
  onstorage: false,
  onsubmit: false,
  onsuspend: false,
  ontimeupdate: false,
  ontoggle: false,
  ontouchcancel: false,
  ontouchend: false,
  ontouchmove: false,
  ontouchstart: false,
  ontransitioncancel: false,
  ontransitionend: false,
  ontransitionrun: false,
  ontransitionstart: false,
  onunhandledrejection: false,
  onunload: false,
  onvolumechange: false,
  onwaiting: false,
  onwaitingforkey: false,
  onwebkitanimationend: false,
  onwebkitanimationiteration: false,
  onwebkitanimationstart: false,
  onwebkittransitionend: false,
  onwheel: false,
  paste: false,
  pause: false,
  play: false,
  playing: false,
  pointercancel: false,
  pointerdown: false,
  pointerenter: false,
  pointerleave: false,
  pointerlockchange: false,
  pointerlockerror: false,
  pointermove: false,
  pointerout: false,
  pointerover: false,
  pointerup: false,
  progress: false,
  ratechange: false,
  readystatechange: false,
  removeEventListener: false,
  reset: false,
  resize: false,
  scroll: false,
  scrollend: false,
  securitypolicyviolation: false,
  seeked: false,
  seeking: false,
  select: false,
  selectionchange: false,
  selectstart: false,
  slotchange: false,
  stalled: false,
  submit: false,
  suspend: false,
  timeupdate: false,
  toggle: false,
  touchcancel: false,
  touchend: false,
  touchmove: false,
  touchstart: false,
  transitioncancel: false,
  transitionend: false,
  transitionrun: false,
  transitionstart: false,
  visibilitychange: false,
  volumechange: false,
  waiting: false,
  webkitanimationend: false,
  webkitanimationiteration: false,
  webkitanimationstart: false,
  webkittransitionend: false,
  wheel: false,
};

/**
 * NB: add/removeEventListeners will be in this set too but it shouldn't affect anything
 */
const badAttributes = new Set(Object.keys(badAttributeMap));

// Used by MUT_ATTR to decide when to skip reifying an attribute. Assumes that
// tagName and attrName are already normalized to lowercase.
export function isBadAttribute(
  tagName: NormalizedTagName,
  attrName: NormalizedAttributeName,
): boolean {
  // Remove attributes of the form "onfoo", because they're DOM0 events.
  if (badAttributes.has(attrName)) {
    return true;
  }

  switch (tagName) {
    case 'iframe':
      if (RestrictedAttrValues.includes(attrName as RestrictedAttr)) {
        // This is an attribute that we consider unsafe to change on playback
        // iframes. See RestrictedAttrValues for more details.
        return true;
      }
      if (attrName === 'loading') {
        // Setting <iframe loading="lazy"> can cause the load event to never be
        // delivered for an iframe, breaking playback.
        return true;
      }
      break;
    case 'frame':
      if (attrName == 'src' || attrName == 'srcdoc') {
        return true;
      }
      break;
    case 'link':
      // Skip weird things like crossorigin=anonymous, that can interfere with
      // resource fetch fallback cases.
      if (attrName === 'crossorigin') {
        return true;
      }
      if (attrName == 'integrity') {
        return true;
      }
      break;
    case 'meta':
      // We want to avoid "http-equiv". A value of "refresh" will refresh the
      // page, and "Content-Security-Policy" can block resources.
      if (attrName == 'http-equiv') {
        return true;
      }
      break;
    case 'script':
    case 'noscript':
      // Absolutely no attributes are allowed on these tags. We should never
      // even encounter <script> elements. <noscript> elements are what we
      // rewrite suspicious elements into and thus must also be viewed with
      // suspicion.
      return true;
  }

  // Seems ok to let through.
  return false;
}
